import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select/dist/declarations/src/types';

import { useOnClickOutside } from 'usehooks-ts';

import { SingleSelect } from '@/common';
import { UploadButton } from '@/components/common';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { UPLOAD_PICKER_TYPES } from '@/constants';
import { AssetScope, AssetUploadType } from '@/enums';
import { getBackgroundUploadOptions } from '@/helpers';
import { useFileUpload, useUploadFilePicker } from '@/hooks';
import { DeleteImageXIcon as DeleteImage } from '@/resources';
import { getTestId, TestIds } from '@/shared/test-ids';
import { Asset, FilePickerType, SelectOptionProps } from '@/types';

type CoverPhotoAssetPanelProps = {
  eventId: string;
  organizationId?: number;
  title: string;
  uploadText: string;
  buttonText: string;
  isBackgroundAsset?: boolean;
  asset: Asset | null;
  isMobileView?: boolean;
  assetTag?: string;
  disableButton?: boolean;
  handleDeleteAsset: (isBackgroundAsset: boolean) => void;
  handleCreatedAsset?: (asset: Asset) => void;
  handleOpenModal?: (usePublicAssets: boolean) => void;
};

export const CoverPhotoAssetPanel = ({
  eventId,
  organizationId,
  title,
  uploadText,
  buttonText,
  isBackgroundAsset,
  asset,
  isMobileView,
  assetTag,
  disableButton,
  handleDeleteAsset,
  handleCreatedAsset,
  handleOpenModal
}: CoverPhotoAssetPanelProps) => {
  const [t] = useTranslation('common');

  const [isDragging, setIsDragging] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const selectRef = useRef<HTMLDivElement>(null);

  const pickerTypes = useMemo<FilePickerType[]>(() => {
    return isBackgroundAsset ? UPLOAD_PICKER_TYPES.BACKGROUND : UPLOAD_PICKER_TYPES.IMAGE;
  }, [isBackgroundAsset]);

  const { uploadFiles, isUploading } = useFileUpload({
    eventId,
    organizationId,
    pickerTypes,
    isPublic: false,
    assetScope: AssetScope.PRIVATE,
    assetTag,
    onCreatedAsset: handleCreatedAsset
  });

  const { openFilePicker, hiddenInputProps } = useUploadFilePicker({
    pickerTypes,
    multiple: false,
    disabled: isUploading,
    onFilesSelected: uploadFiles
  });

  useOnClickOutside([selectRef], () => setIsMenuOpen(false));

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    await uploadFiles(Array.from(event.dataTransfer.files));
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleBackgroundSelectChange = async (selectedOption: SingleValue<SelectOptionProps>) => {
    if (!handleOpenModal) {
      return;
    }

    setIsMenuOpen(false);

    if (selectedOption?.value === AssetUploadType.UPLOAD_NEW) {
      await openFilePicker();
      return;
    }

    handleOpenModal(selectedOption?.value === AssetUploadType.PUBLIC_ASSET);
  };

  const displayedAssetProps = {
    src: asset?.thumbnailUrl ?? '',
    className: 'object-cover w-28 h-28 3xl:w-36 3xl:h-36 rounded-md 4xl:w-36 4xl:h-36'
  };

  return (
    <div
      data-testid={getTestId(TestIds.coverPhotos.assetPanel, title)}
      className='my-3.5 md:my-6'
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}>
      <input {...hiddenInputProps} />

      <div className='mx-3 md:mx-6'>
        <Label text={t(title)} type={LABEL_TYPE.H4} />
      </div>

      {asset && !isMobileView ? (
        <div
          className={`mb-6 ml-3 mt-4 flex w-28 flex-col border md:ml-6 ${
            isDragging ? 'w-fit border-dashed border-primary-blue' : 'border-transparent'
          }`}>
          <div className='relative flex size-28 flex-col justify-center 3xl:size-36 4xl:size-36'>
            <img data-testid={TestIds.coverPhotos.assetImage} {...displayedAssetProps} />

            <div className='absolute right-2 top-2 z-10 flex size-7 cursor-pointer items-center justify-center rounded-md bg-white/[0.6] hover:bg-white'>
              <DeleteImage
                data-testid={TestIds.coverPhotos.deleteAssetButton}
                onClick={() => handleDeleteAsset(!!isBackgroundAsset)}
              />
            </div>
          </div>
        </div>
      ) : null}

      {asset && isMobileView ? (
        <div className='mx-3 mb-6 mt-4 flex h-9 flex-row items-center justify-between rounded-lg border border-accent-light-gray bg-primary-white'>
          <Label text={asset.assetName} type={LABEL_TYPE.PARAGRAPH2} className='ml-4 text-xs' />

          <div className='flex h-3.5 cursor-pointer items-center rounded-md bg-white/[0.6] pr-2.5 hover:bg-white'>
            <DeleteImage
              data-testid={TestIds.coverPhotos.deleteAssetButton}
              onClick={() => handleDeleteAsset(!!isBackgroundAsset)}
              className='size-5'
            />
          </div>
        </div>
      ) : null}

      {asset || isMobileView ? null : (
        <div
          className={`mt-4 cursor-pointer border text-center md:mx-14 3xl:mx-20 4xl:mx-20 ${
            isDragging ? 'border-dashed border-primary-blue' : 'border-transparent'
          }`}>
          <Label
            text={t(uploadText)}
            type={LABEL_TYPE.PARAGRAPH2}
            className='text-secondary-gray'
          />
        </div>
      )}

      <div className='mx-3 md:mx-6'>
        {!isBackgroundAsset && (
          <>
            <Button
              data-testid={TestIds.coverPhotos.choosePhotoButton}
              variant={ButtonVariant.SECONDARY}
              className={`mt-4 h-9 w-full ${disableButton && 'text-secondary-gray'}`}
              handleOnClick={() => handleOpenModal?.(false)}
              disabled={disableButton}>
              {t('choosePhoto')}
            </Button>

            <div data-testid={TestIds.coverPhotos.uploadButton}>
              <UploadButton
                text={buttonText}
                pickerTypes={pickerTypes}
                multiple={false}
                isUploading={isUploading}
                icon={null}
                showMaxFileSize={false}
                className='mt-4 h-9 w-full cursor-pointer justify-center'
                onFilesSelected={uploadFiles}
              />
            </div>
          </>
        )}

        {isBackgroundAsset && (
          <div ref={selectRef}>
            <SingleSelect
              id='backgroundImage'
              value={null}
              placeholder={isUploading ? t('uploading') : t('changeBackground')}
              options={getBackgroundUploadOptions() as unknown as SelectOptionProps[]}
              containerClassName='mt-4'
              handleChange={handleBackgroundSelectChange}
              onMenuOpen={() => setIsMenuOpen(true)}
              onMenuClose={() => setIsMenuOpen(false)}
              isLiveStream
              menuIsOpen={isMenuOpen}
              disabled={isUploading}
            />
          </div>
        )}
      </div>
    </div>
  );
};
